import { useContext, useEffect, useState } from 'react';
import { UNSAFE_NavigationContext, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Tab, Tabs, TextField, Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryIcon from '@mui/icons-material/History';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { axiosInstance, axiosPrivate } from '../../../../../api/axios';
import { RegisterCause } from '../../register/cause';
import { getRiskStandardByScore } from '../../../utils/riskStandard';
import { ManualHelpButton } from '../../../components/common/HelperFunc';
import './stepZero.css';

const isHex = value => /^#[0-9a-f]{6}$/i.test(value || '');
const historyLabels = { safe_cal_frequency: '가능성', safe_cal_importancy: '중대성', safe_cal_standard: '위험도', tbm_session: 'TBM', msds_management: 'MSDS', safe_register_cause: '설비 위험요인', safety_inspection: '안전점검', safety_action: '후속 조치' };

function ScoreTable({ title, count, rows, levelKey, descriptionKey, editing, busy, onChange }) {
  return <section className="step-zero-score-section">
    <div className="step-zero-section-heading"><h2>{title}</h2><span className="step-zero-meta">{count}단계</span></div>
    <div className="step-zero-score-table">
      {rows.map((row, index) => <div className="step-zero-score-row" key={row[levelKey]}>
        <span className="step-zero-level">{row[levelKey]}<small>점</small></span>
        {editing ? <TextField fullWidth size="small" multiline maxRows={3} disabled={busy}
          value={row[descriptionKey] || ''} inputProps={{ maxLength: 50, 'aria-label': `${title} ${row[levelKey]}점 설명` }}
          onChange={event => onChange(index, descriptionKey, event.target.value)} />
          : <span className="step-zero-description">{row[descriptionKey] || '설명 없음'}</span>}
      </div>)}
    </div>
  </section>;
}

export function AssessmentStep0Page() {
  const { group_code, project_code } = useParams();
  const navigate = useNavigate();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const endpoint = `/risk/${group_code}/${project_code}/wizard/risk-criteria`;
  const [tab, setTab] = useState(0);
  const [criteria, setCriteria] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    let active = true;
    setCanEdit(false);
    setLoading(true);
    setCriteria(null);
    setSaved(null);
    setEditing(false);
    setHistoryOpen(false);
    setHistory([]);
    setMessage(null);
    axiosPrivate.get(`${endpoint}/edit-permission`).then(({ data }) => {
      if (active) {
        setCanEdit(data.can_edit === true);
        setEditing(data.can_edit === true);
      }
    }).catch(error => {
      if (active) setMessage({ severity: 'warning', text: error.response?.data?.message || '편집 권한을 확인하지 못했습니다. 로그인 상태를 확인한 뒤 새로고침해 주세요.' });
    });
    axiosInstance.get(endpoint).then(({ data }) => {
      if (active) { setCriteria(data); setSaved(data); }
    }).catch(() => active && setMessage({ severity: 'error', text: '평가기준을 불러오지 못했습니다. 페이지를 새로고침해 주세요.' }))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [endpoint]);

  const update = (key, index, field, value) => setCriteria(current => ({ ...current,
    [key]: current[key].map((row, i) => i === index ? { ...row, [field]: value } : row),
  }));
  const dirty = Boolean(criteria && JSON.stringify(criteria) !== JSON.stringify(saved));
  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = event => { event.preventDefault(); event.returnValue = ''; };
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    navigator.push = (...args) => setPendingNavigation({ proceed: () => originalPush.apply(navigator, args) });
    navigator.replace = (...args) => setPendingNavigation({ proceed: () => originalReplace.apply(navigator, args) });
    const currentIndex = window.history.state?.idx;
    let restoring = false;
    let leaving = false;
    // BrowserRouter has no data-router blocker: cancel traversal before its popstate listener.
    const onPopState = event => {
      if (leaving) return;
      if (restoring) { restoring = false; return; }
      const nextIndex = event.state?.idx;
      if (Number.isInteger(currentIndex) && Number.isInteger(nextIndex) && currentIndex !== nextIndex) {
        event.stopImmediatePropagation();
        restoring = true;
        window.history.go(currentIndex - nextIndex);
        setPendingNavigation({ proceed: () => { leaving = true; window.history.go(nextIndex - currentIndex); } });
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('popstate', onPopState, true);
    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('popstate', onPopState, true);
    };
  }, [dirty, navigator]);
  // Stored invalid colors retain the existing fallback; validate only a changed color.
  const invalidColor = criteria?.standards.some((row, index) =>
    row.hex_color !== saved?.standards[index]?.hex_color && row.hex_color && !isHex(row.hex_color));
  const save = async () => {
    setBusy(true);
    try {
      await axiosPrivate.put(endpoint, criteria);
      setSaved(criteria);
      setEditing(false);
      setHistoryOpen(false);
      window.dispatchEvent(new Event('safe-criteria-updated'));
      setMessage({ severity: 'success', text: '변경한 평가기준이 반영되었습니다.' });
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally { setBusy(false); }
  };
  const toggleHistory = async () => {
    setHistoryOpen(!historyOpen);
    if (historyOpen) return;
    setHistoryLoading(true);
    try { const { data } = await axiosPrivate.get(`${endpoint}/history`); setHistory(data); }
    catch { setMessage({ severity: 'error', text: '변경 이력을 불러오지 못했습니다.' }); }
    finally { setHistoryLoading(false); }
  };

  return <Box className="safe-step-zero" sx={{ minHeight: '100%', p: { xs: 1.5, md: 3 }, bgcolor: '#f6f8fb' }}>
    <Dialog open={Boolean(pendingNavigation)} onClose={() => setPendingNavigation(null)} maxWidth="xs" fullWidth>
      <DialogTitle>저장하지 않은 변경사항</DialogTitle>
      <DialogContent>변경사항을 버리고 이동할까요?</DialogContent>
      <DialogActions>
        <Button onClick={() => setPendingNavigation(null)}>계속 편집</Button>
        <Button color="error" onClick={() => { const pending = pendingNavigation; setPendingNavigation(null); pending?.proceed(); }}>버리고 이동</Button>
      </DialogActions>
    </Dialog>
    <div className="step-zero-container">
    <header className="step-zero-header">
      <div className="step-zero-title-group">
        <span className="step-zero-title-icon"><TuneIcon /></span>
        <div>
          <div className="step-zero-title-line"><h1>STEP 0 평가 준비</h1><ManualHelpButton title="STEP 0 평가 준비" /></div>
          <p className="step-zero-subtitle">평가기준과 설비별 위험요인을 확인하고 위험성평가를 준비합니다.</p>
        </div>
      </div>
      <Button variant="contained" endIcon={<ArrowForwardIcon />} disabled={busy || dirty}
        onClick={() => navigate(`/safe/${group_code}/${project_code}/assessment/wizard/step-1`)}>위험성평가 시작</Button>
    </header>
    <div className="step-zero-content">
    <Tabs className="step-zero-tabs" value={tab} onChange={(_, value) => setTab(value)} aria-label="평가 준비 항목">
      <Tab id="step-zero-criteria-tab" aria-controls="step-zero-criteria" icon={<TuneIcon />} iconPosition="start" label="평가기준" />
      <Tab id="step-zero-hazards-tab" aria-controls="step-zero-hazards" icon={<PrecisionManufacturingIcon />} iconPosition="start" label="설비별 위험요인" />
    </Tabs>
    {message && <Alert className="step-zero-alert" severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}

    {tab === 0 && <div role="tabpanel" id="step-zero-criteria" aria-labelledby="step-zero-criteria-tab">
      {loading ? <div className="step-zero-empty"><CircularProgress size={26} /></div> : criteria && <>
        <div className="step-zero-toolbar">
          <div className="step-zero-formula"><span className="step-zero-step">STEP 0</span><strong>가능성 5단계</strong><span>×</span><strong>중대성 4단계</strong><span>=</span><strong>위험도 1~20점</strong></div>
          <div className="step-zero-actions">
            {editing ? <>
              {dirty && <span className="step-zero-unsaved">저장 전 변경사항</span>}
              <Button disabled={busy} onClick={() => { setCriteria(saved); setEditing(false); }}>취소</Button>
              <Button startIcon={<SaveOutlinedIcon />} variant="contained" disabled={busy || !dirty || invalidColor} onClick={save}>{busy ? '저장 중' : '저장'}</Button>
            </> : canEdit ? <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setEditing(true)}>기준 편집</Button> : <span className="step-zero-meta">조회 전용</span>}
          </div>
        </div>
        {invalidColor && <Alert severity="error" sx={{ mb: 2 }}>색상은 #RRGGBB 형식으로 입력하세요.</Alert>}
        <div className="step-zero-workspace">
          <section className="step-zero-risk-section">
            <div className="step-zero-section-heading step-zero-matrix-heading"><h2>위험도 매트릭스</h2><span className="step-zero-meta">{editing ? '편집 미리보기' : '현재 적용 기준'}</span></div>
            <div className="step-zero-matrix-wrap">
              <table className="step-zero-matrix" aria-label="가능성 및 중대성별 위험도">
                <thead><tr><th scope="col"><span>가능성 ↓</span><span>중대성 →</span></th>{[1, 2, 3, 4].map(value => <th scope="col" key={value}>{value}<small>점</small></th>)}</tr></thead>
                <tbody>{[5, 4, 3, 2, 1].map(frequency => <tr key={frequency}>
                  <th scope="row">{frequency}<small>점</small></th>
                  {[1, 2, 3, 4].map(importance => {
                    const score = frequency * importance;
                    const style = getRiskStandardByScore(score, criteria.standards);
                    return <td key={importance}><Tooltip title={`${score}점 · ${style.label}`} arrow>
                      <div className="step-zero-matrix-score" tabIndex={0} aria-label={`가능성 ${frequency}점, 중대성 ${importance}점, 위험도 ${score}점: ${style.label}`}
                        style={{ background: style.bg, color: style.fg, borderColor: style.border }}>
                        <strong>{score}</strong><span className="step-zero-matrix-label">{style.label}</span>
                      </div>
                    </Tooltip></td>;
                  })}
                </tr>)}</tbody>
              </table>
            </div>
            <div className="step-zero-section-heading step-zero-result-heading"><h2>점수별 조치 기준</h2></div>
            <div className="step-zero-standards">{criteria.standards.map((row, index) => {
              const style = getRiskStandardByScore(row.risk_level_min, criteria.standards);
              const label = `${row.risk_level_min}~${row.risk_level_max}점`;
              return <div className={`step-zero-standard ${editing ? 'is-editing' : ''}`} key={row.standard_id}>
                <span className="step-zero-range"><i style={{ background: style.solid }} />{label}</span>
                {editing ? <>
                  <TextField fullWidth multiline maxRows={3} size="small" disabled={busy} value={row.manage_criteria || ''}
                    inputProps={{ maxLength: 50, 'aria-label': `${label} 조치 기준` }} onChange={event => update('standards', index, 'manage_criteria', event.target.value)} />
                  <div className="step-zero-color-editor">
                    <input type="color" disabled={busy} aria-label={`${label} 색상 선택`} value={isHex(row.hex_color) ? row.hex_color : style.solid} onChange={event => update('standards', index, 'hex_color', event.target.value)} />
                    <TextField size="small" disabled={busy} value={row.hex_color || ''} placeholder="기본 색상"
                      inputProps={{ maxLength: 7, 'aria-label': `${label} HEX 색상` }} error={Boolean(row.hex_color && !isHex(row.hex_color))}
                      onChange={event => update('standards', index, 'hex_color', event.target.value)} />
                  </div>
                </> : <span className="step-zero-description">{style.label}</span>}
              </div>;
            })}</div>
          </section>
          <div className="step-zero-score-sections">
            <ScoreTable title="가능성" count={5} rows={criteria.frequencies} levelKey="frequency_level" descriptionKey="frequency_description" editing={editing} busy={busy} onChange={(index, field, value) => update('frequencies', index, field, value)} />
            <ScoreTable title="중대성" count={4} rows={criteria.importancies} levelKey="importancy_level" descriptionKey="importancy_description" editing={editing} busy={busy} onChange={(index, field, value) => update('importancies', index, field, value)} />
          </div>
        </div>
      </>}
    </div>}

    {tab === 1 && <div role="tabpanel" id="step-zero-hazards" aria-labelledby="step-zero-hazards-tab" className="step-zero-hazards"><RegisterCause embedded /></div>}
    {canEdit && <section className="step-zero-history">
      <Button startIcon={<HistoryIcon />} endIcon={<ExpandMoreIcon sx={{ transform: historyOpen ? 'rotate(180deg)' : 'none' }} />} onClick={toggleHistory} aria-expanded={historyOpen}>변경 이력</Button>
      {historyOpen && (historyLoading ? <div className="step-zero-empty"><CircularProgress size={22} /></div> : history.length === 0 ? <div className="step-zero-empty">변경 이력이 없습니다.</div> : <div className="step-zero-history-table"><table>
        <thead><tr><th>변경 시각</th><th>대상</th><th>변경자</th><th>변경 내용</th></tr></thead>
        <tbody>{history.map(row => <tr key={row.id}>
          <td>{new Date(row.changed_at).toLocaleString('ko-KR')}</td><td>{historyLabels[row.entity_type] || row.entity_type} #{row.entity_id}</td><td>{row.actor_id || '-'}</td>
          <td><details><summary>상세 보기</summary><pre>{`${row.before_json || '-'}\n→\n${row.after_json}`}</pre></details></td>
        </tr>)}</tbody>
      </table></div>)}
    </section>}
    </div>
    </div>
  </Box>;
}
